import React from 'react';
import { Clock, AlertTriangle, Send, Upload, Home, Target, ChevronRight, Trophy, MinusCircle } from 'lucide-react';
import { motion } from 'framer-motion';
export function PointsReference() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.4
  }} className="flex flex-col gap-6" data-id="element-198">
      {/* Deductions & Warnings */}
      <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-199">
        <div className="bg-red-50 px-4 py-3 border-b border-red-100 flex items-center gap-2" data-id="element-200">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" data-id="element-201" />
          <h3 className="font-bold text-red-800 uppercase tracking-wide text-sm" data-id="element-202">
            Point Deductions
          </h3>
        </div>
        <div className="grid md:grid-cols-3 md:divide-x divide-y md:divide-y-0 divide-gray-100" data-id="element-203">
          {/* Time Missed Header */}
          <div className="flex items-start gap-3 p-5 min-w-0" data-id="element-204">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-205">
              <MinusCircle className="w-5 h-5 text-red-600" data-id="element-206" />
            </div>
            <div className="min-w-0" data-id="element-207">
              <h4 className="text-sm font-bold text-gray-800 uppercase leading-tight" data-id="element-208">
                Time Missed (Non PTO)
              </h4>
              <p className="text-xs text-gray-600 mt-1 leading-snug" data-id="element-209">
                Point deduction for each hour. Rounded down to nearest hour.
              </p>
            </div>
          </div>

          {/* Time Tier Rates */}
          <div className="flex flex-col justify-center p-5 gap-3 min-w-0" data-id="element-210">
            <div className="flex items-center justify-between gap-3" data-id="element-211">
              <div className="flex items-center gap-2 min-w-0" data-id="element-212">
                <Clock className="w-4 h-4 text-gray-400 shrink-0" data-id="element-213" />
                <span className="text-xs font-semibold text-gray-600 truncate" data-id="element-214">
                  Up to 15 Hours
                </span>
              </div>
              <div className="text-right shrink-0 whitespace-nowrap" data-id="element-215">
                <span className="text-lg font-bold text-red-600" data-id="element-216">100</span>
                <span className="text-[10px] font-bold text-gray-500 ml-1" data-id="element-217">
                  PTS / HR
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-218">
              <div className="flex items-center gap-2 min-w-0" data-id="element-219">
                <Clock className="w-4 h-4 text-red-400 shrink-0" data-id="element-220" />
                <span className="text-xs font-semibold text-red-600 truncate" data-id="element-221">
                  Above 15 Hours
                </span>
              </div>
              <div className="text-right shrink-0 whitespace-nowrap" data-id="element-222">
                <span className="text-lg font-bold text-red-600" data-id="element-223">150</span>
                <span className="text-[10px] font-bold text-gray-500 ml-1" data-id="element-224">
                  PTS / HR
                </span>
              </div>
            </div>
          </div>

          {/* File Not Moving */}
          <div className="flex items-start gap-3 p-5 min-w-0" data-id="element-225">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-226">
              <AlertTriangle className="w-5 h-5 text-red-500" data-id="element-227" />
            </div>
            <div className="min-w-0" data-id="element-228">
              <h4 className="text-sm font-bold text-gray-800 uppercase leading-tight" data-id="element-229">
                File Not Moving
              </h4>
              <div className="flex items-baseline gap-1 mt-1 flex-wrap" data-id="element-230">
                <span className="text-xl font-bold text-red-600 leading-none" data-id="element-231">
                  -50
                </span>
                <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-232">
                  Pts Per Day
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-snug" data-id="element-233">
                For each file in "Not Moving" status.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Process Footer */}
      <div className="bg-[#0a1e5e] rounded-xl shadow-sm overflow-hidden text-white" data-id="element-234">
        <div className="text-center py-2 bg-black/20 text-xs font-bold tracking-widest uppercase text-blue-200" data-id="element-235">
          The Process That Wins
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] gap-3 items-center" data-id="element-236">
          <ProcessStep num="1" title="Create" sub="Get credit pulls." icon={<Target className="w-5 h-5 text-blue-300" data-id="element-238" />} iconWrapClass="border-2 border-blue-400 bg-transparent" data-id="element-237" />
          <ProcessStep num="2" title="Convert" sub="Send doc outs." icon={<Send className="w-5 h-5 text-white" data-id="element-240" />} iconWrapClass="bg-green-500" data-id="element-239" />
          <ProcessStep num="3" title="Submit" sub="Get loans submitted." icon={<Upload className="w-5 h-5 text-white" data-id="element-242" />} iconWrapClass="bg-purple-500" data-id="element-241" />
          <ProcessStep num="4" title="Close" sub="Close loans fast." icon={<Home className="w-5 h-5 text-white" data-id="element-244" />} iconWrapClass="bg-orange-500" data-id="element-243" />

          <div className="hidden lg:flex items-center gap-3 pl-4 border-l border-blue-800/60 min-w-0" data-id="element-245">
            <Trophy className="w-8 h-8 text-yellow-400 shrink-0" data-id="element-246" />
            <div className="min-w-0" data-id="element-247">
              <p className="text-sm font-bold uppercase leading-tight" data-id="element-248">
                Every Action
              </p>
              <p className="text-sm font-bold uppercase leading-tight text-blue-300" data-id="element-249">
                Drives Your Score.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}
interface ProcessStepProps {
  num: string;
  title: string;
  sub: string;
  icon: React.ReactNode;
  iconWrapClass: string;
}
function ProcessStep({
  num,
  title,
  sub,
  icon,
  iconWrapClass
}: ProcessStepProps) {
  return <div className="flex items-center gap-3 min-w-0" data-id="element-250">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconWrapClass}`} data-id="element-251">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-252">
        <p className="text-sm font-bold uppercase leading-tight truncate" data-id="element-253">
          {num}. {title}
        </p>
        <p className="text-xs text-blue-200 leading-tight mt-0.5 truncate" data-id="element-254">
          {sub}
        </p>
      </div>
    </div>;
}